/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import {
    ArrowRight,
    CheckCircle,
    MessageCircle,
    PhoneCall,
    Sparkles,
} from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import Page from '@/client/pages/_layout/_page';
import type { BookDemoCallbackType, BookDemoLeadPayload } from '@/common/types/marketing/bookDemo';

/*----------------------------------
- TYPES
----------------------------------*/

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    message: string;
    preferredSlot: string;
}

/*----------------------------------
- CONSTANTS
----------------------------------*/

const METRICS = [
    { value: '70%', label: 'Touchpoints automated' },
    { value: '5–7h', label: 'Time saved / recruiter each week' },
    { value: '6+', label: 'Standalone tools replaced' },
];

const TOOL_HIGHLIGHTS = [
    {
        title: 'AI Copilots',
        description: 'Automated sourcing, enrichment, and next-step suggestions for every candidate.',
    },
    {
        title: 'Unified workflows',
        description: 'Email, WhatsApp, and LinkedIn cadences orchestrated from one canvas.',
    },
    {
        title: 'Real-time reporting',
        description: 'Pipeline visibility, forecasting, and SLA alerts for every client project.',
    },
];

const INITIAL_FORM_DATA: FormData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    preferredSlot: '',
};

const submitBookDemoLead = async (payload: BookDemoLeadPayload) => {
    const response = await fetch('/leads/book-demo', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        const errorMessage = (await response.text()).trim();
        throw new Error(errorMessage || 'Unable to send your request right now.');
    }
};

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function SaaSInboundLanding() {
    const [callbackType, setCallbackType] = React.useState<BookDemoCallbackType>('video');
    const [formData, setFormData] = React.useState<FormData>(INITIAL_FORM_DATA);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState('');

    const handleChange = (field: keyof FormData, value: string) => {
        setFormData(previous => ({ ...previous, [field]: value }));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitError('');
        setIsSubmitting(true);

        try {
            await submitBookDemoLead({
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                callbackType,
                message: formData.message.trim() || undefined,
                preferredSlot: callbackType === 'video' ? formData.preferredSlot.trim() || undefined : undefined,
            });

            setIsSubmitted(true);
            setFormData(INITIAL_FORM_DATA);

            window.setTimeout(() => {
                setIsSubmitted(false);
            }, 3000);
        } catch (reason) {
            setSubmitError(reason instanceof Error ? reason.message : 'Unable to send your request right now.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Page withLayout={false}>
            <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-pink-50 text-slate-900">
                <header className="border-b border-orange-100 bg-white/90 backdrop-blur sticky top-0 z-30">
                    <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
                        <div>
                            <p className="text-xs uppercase tracking-[0.5em] text-orange-500">CrossPath SaaS</p>
                            <p className="text-lg font-bold text-slate-800">Automations & AI copilots</p>
                        </div>
                        <a
                            href="#callback-form"
                            className="px-5 py-2 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-orange-500 via-pink-500 to-fuchsia-600 shadow-md hover:shadow-lg"
                        >
                            Book a demo
                        </a>
                    </div>
                </header>

                <main className="max-w-5xl mx-auto px-6 py-14 space-y-16">
                    <section className="space-y-6 text-center">
                        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.5em] text-orange-500 justify-center">
                            <Sparkles className="w-4 h-4" />
                            From LinkedIn inspiration to live product
                        </p>
                        <h1 className="text-4xl lg:text-5xl font-black leading-tight">
                            Give every recruiter a copilot and a multichannel control tower.
                        </h1>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            You saw the workflows on LinkedIn—this is where you continue the conversation. CrossPath SaaS unifies
                            sourcing, outreach, enrichment, and reporting so you can scale personalized engagement without adding more
                            tools.
                        </p>
                    </section>

                    <section id="callback-form" className="bg-white border border-orange-100 rounded-3xl p-8 shadow-lg">
                        <div className="text-center space-y-2 mb-8">
                            <p className="text-xs uppercase tracking-[0.4em] text-orange-500">Request your callback</p>
                            <h2 className="text-2xl font-bold">Video tour or WhatsApp message—pick what's easiest.</h2>
                            <p className="text-sm text-slate-600 max-w-3xl mx-auto">
                                People discover us on social every day. Keep the momentum going by selecting how you want us to follow up.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <button
                                type="button"
                                onClick={() => setCallbackType('video')}
                                className={`p-4 rounded-2xl border-2 transition-all ${
                                    callbackType === 'video'
                                        ? 'border-orange-500 bg-orange-50'
                                        : 'border-slate-200 bg-white hover:border-orange-200'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <PhoneCall className={`w-6 h-6 ${callbackType === 'video' ? 'text-orange-500' : 'text-slate-400'}`} />
                                    <div className="text-left">
                                        <h3 className="font-bold text-slate-900">15-min video call</h3>
                                        <p className="text-xs text-slate-600">Share your stack, see the copilots live</p>
                                    </div>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setCallbackType('whatsapp')}
                                className={`p-4 rounded-2xl border-2 transition-all ${
                                    callbackType === 'whatsapp'
                                        ? 'border-orange-500 bg-orange-50'
                                        : 'border-slate-200 bg-white hover:border-orange-200'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <MessageCircle
                                        className={`w-6 h-6 ${callbackType === 'whatsapp' ? 'text-orange-500' : 'text-slate-400'}`}
                                    />
                                    <div className="text-left">
                                        <h3 className="font-bold text-slate-900">WhatsApp message</h3>
                                        <p className="text-xs text-slate-600">Quick brief, reply in under 2h</p>
                                    </div>
                                </div>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">First name *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.firstName}
                                        onChange={(event) => handleChange('firstName', event.currentTarget.value)}
                                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                        placeholder="John"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Last name *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.lastName}
                                        onChange={(event) => handleChange('lastName', event.currentTarget.value)}
                                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Work email *</label>
                                    <input
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={(event) => handleChange('email', event.currentTarget.value)}
                                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                        placeholder="john@company.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone / WhatsApp *</label>
                                    <input
                                        type="tel"
                                        required
                                        value={formData.phone}
                                        onChange={(event) => handleChange('phone', event.currentTarget.value)}
                                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                        placeholder="+33 6 00 00 00 00"
                                    />
                                </div>
                            </div>

                            {callbackType === 'video' && (
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Preferred time slot (optional)</label>
                                    <input
                                        type="datetime-local"
                                        value={formData.preferredSlot}
                                        onChange={(event) => handleChange('preferredSlot', event.currentTarget.value)}
                                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Message (optional)</label>
                                <textarea
                                    rows={3}
                                    value={formData.message}
                                    onChange={(event) => handleChange('message', event.currentTarget.value)}
                                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                    placeholder="Share context about your team, current tools, or what you'd like to see..."
                                />
                            </div>

                            {isSubmitted ? (
                                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                                    <p className="text-sm font-semibold text-green-700">
                                        ✓ Request sent! We'll contact you within 2 business hours.
                                    </p>
                                </div>
                            ) : submitError ? (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                                    <p className="text-sm font-semibold text-red-700">{submitError}</p>
                                </div>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full rounded-2xl bg-gradient-to-r from-orange-500 via-pink-500 to-fuchsia-600 text-white font-semibold py-3 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting
                                        ? 'Sending...'
                                        : callbackType === 'video'
                                            ? 'Request video call'
                                            : 'Request WhatsApp callback'}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            )}
                        </form>
                    </section>

                    <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {METRICS.map((metric) => (
                            <div key={metric.label} className="bg-white border border-orange-100 rounded-2xl p-4 shadow-sm">
                                <p className="text-3xl font-black text-slate-900">{metric.value}</p>
                                <p className="text-xs text-slate-500">{metric.label}</p>
                            </div>
                        ))}
                    </section>

                    <section className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm">
                        <h2 className="text-2xl font-bold mb-6 text-slate-900">Why operators switch to CrossPath</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {TOOL_HIGHLIGHTS.map((tool) => (
                                <div key={tool.title} className="space-y-2">
                                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-100 to-pink-100 text-orange-500 flex items-center justify-center">
                                        <CheckCircle className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-lg font-semibold">{tool.title}</h3>
                                    <p className="text-sm text-slate-600">{tool.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                </main>
            </div>
        </Page>
    );
}
